import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Search, X } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import MasterTable from "./MasterTable";
import { getBranches, createBranch, updateBranch, toggleBranchActive } from "@/services/masters";
import AddBranchForm, { BranchFormData } from "./branches/AddBranch";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { useRoleContext } from "@/hooks/useRoleContext";
import MasterTableHeader from "./generics/MasterTableHeader";

interface Branch {
  id?: string | number;
  name: string;
  state: string;
  stateCode: string;
  city: string;  
  cityCode: string;
  zone: string;   
  zoneCode: string; 
  address: string;
  isActive: boolean;
}

export default function Branches() {
  const { canCreate, canUpdate } = useScreenPermission("branches");
  const { isBranchIncharge, isZoneManager, branchCode, zoneCode } = useRoleContext();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [pendingCity, setPendingCity] = useState("");
  const [togglingId, setTogglingId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

 const methods = useForm<BranchFormData>({
  defaultValues: {
    Branch_Name: "", State: "", State_Code: "",
    City: "", City_Code: "", Zone: "", Zone_Code: "",
    Branch_Address: "",
  }
});

  const { setError, clearErrors } = methods;

  const loadBranches = useCallback(async (p: number = 1, search: string = "", statusFilter: string) => {
    setIsLoading(true);
    try {
      const { data, status } = await getBranches(
        p, search,
        isBranchIncharge ? branchCode : undefined,
        isZoneManager ? zoneCode : undefined,
        statusFilter
      );

      if (status === 200 && data) {
        const d = data as any;
        const items: any[] = d.results ?? (Array.isArray(d) ? d : []);

        setBranches(items.map((item: any) => ({
          id: item.id,
          name: item.Branch_Name,
          state: item.State,
          stateCode: item.State_Code,
          city: item.City,
          cityCode: item.City_Code,
          zone: item.Zone,
          zoneCode: item.Zone_Code,
          address: item.Branch_Address,
          isActive: item.Is_Active ?? true,
        })));
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      } else {
        // toast.error("Failed to load branches");
      }
    } catch (error) {
      toast.error("Error loading branches");
    } finally {
      setIsLoading(false);
    }
  }, [isBranchIncharge, isZoneManager, branchCode, zoneCode]);

  useEffect(() => {
    const delay = searchQuery ? 400 : 0;
    const timer = setTimeout(() => {
      setPage(1);
      loadBranches(1, searchQuery, statusFilter);
    }, delay);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, loadBranches]);

  const handleOpenModal = useCallback(
  (branch?: Branch) => {
    clearErrors();

    if (branch) {
      const foundBranch = branches.find((b) => b.id === branch.id);

      if (foundBranch) {
        setPendingCity(foundBranch.cityCode);

        methods.reset({
          Branch_Name: foundBranch.name,

          State_Code: foundBranch.stateCode,
          City_Code: foundBranch.cityCode,
          Zone_Code: foundBranch.zoneCode,

          State: foundBranch.state,
          City: foundBranch.city,
          Zone: foundBranch.zone,

          Branch_Address: foundBranch.address,
        });

        setEditingBranch(foundBranch);
      }
    } else {
      setPendingCity("");

      methods.reset({
        Branch_Name: "",
        State: "",
        City: "",
        Zone: "",
        State_Code: "",
        City_Code: "",
        Zone_Code: "",
        Branch_Address: "",
      });

      setEditingBranch(null);
    }

    setIsModalOpen(true);
  },
  [branches, methods, clearErrors]
);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingBranch(null);
    methods.reset({
  Branch_Name: "",
  State: "",
  City: "",
  Zone: "",
  State_Code: "",
  City_Code: "",
  Zone_Code: "",
  Branch_Address: "",
});
  }, [methods]);

  const handleSubmit = useCallback(async (data: BranchFormData) => {
    clearErrors();
    setIsLoading(true);
    try {
      const response = editingBranch?.id
        ? await updateBranch(Number(editingBranch.id), data)
        : await createBranch(data);

      if (response.status === 201 || response.status === 200) {
        toast.success(editingBranch ? "Branch updated successfully" : "Branch created successfully");
        handleCloseModal();
        await loadBranches(page, searchQuery, statusFilter);
      } else if (response.errors && typeof response.errors === "object") {
        let hasFieldErrors = false;
        Object.entries(response.errors).forEach(([key, value]) => {
          const errorText = Array.isArray(value) ? value[0] : String(value);
          if (key === "error" || key === "non_field_errors" || key === "detail") {
            toast.error(errorText);
          } else {
            setError(key as keyof BranchFormData, { type: "server", message: errorText });
            hasFieldErrors = true;
          }
        });
        if (!hasFieldErrors) toast.error("Failed to save branch");
      } else {
        toast.error("Failed to save branch");
      }
    } catch (error) {
      toast.error("Error saving branch");
    } finally {
      setIsLoading(false);
    }
  }, [editingBranch, handleCloseModal, loadBranches, setError, clearErrors, page, searchQuery]);

  const handleToggleActive = useCallback(async (branch: Branch) => {
    if (!branch.id) return;
    setTogglingId(branch.id);
    try {
      const response = await toggleBranchActive(Number(branch.id), { Is_Active: !branch.isActive });
      if (response.status === 200) {
        toast.success(`Branch ${!branch.isActive ? "activated" : "deactivated"} successfully`);
        await loadBranches(page, searchQuery, statusFilter);
      } else {
        toast.error("Failed to update branch status");
      }
    } catch (error) {
      toast.error("Error updating branch status");
    } finally {
      setTogglingId(null);
    }
  }, [loadBranches, page, searchQuery, statusFilter]);

  const columns = useMemo(() => [
    { key: "name" as const, label: "Branch Name" },
    { key: "state" as const, label: "State" },
    { key: "city" as const, label: "District" },
    { key: "zone" as const, label: "Zone" },
    { key: "address" as const, label: "Address" },
  ], []);

  return (
    <div className="space-y-6">
      <MasterTableHeader
        title="Branches Management"
        description="Create, edit, and manage branches"
        canCreate={canCreate}
        addButtonText="Add New Branch"
        onAdd={() => handleOpenModal()}
        searchPlaceholder="Search by branch or state..."
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showDropdownFilter
        dropdownValue={statusFilter}
        onDropdownChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}
        showClearFilters
        onClearFilters={() => {
          setSearchQuery("");
          setStatusFilter("");
          setPage(1);

          loadBranches(1, "", "");
        }}
      />

      <MasterTable
        columns={columns}
        data={branches}
        onEdit={handleOpenModal}
        onToggleActive={handleToggleActive}
        togglingId={togglingId}
        isLoading={isLoading && branches.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadBranches(p, searchQuery, statusFilter); }}
        canEdit={canUpdate}
        canToggle={canUpdate}
        totalCount={totalCount}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingBranch ? "Edit Branch" : "Add New Branch"}
        onClose={handleCloseModal}
        onSubmit={methods.handleSubmit(handleSubmit)}
        isLoading={isLoading}
      >
        <FormProvider {...methods}>
          <AddBranchForm pendingCity={pendingCity} />
        </FormProvider>
      </MasterModal>
    </div>
  );
}
